import React from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { usePrevious } from 'react-use'

import { RecipeCoreId } from '@library/menu-service'

import { actionTypes } from 'actions/actionTypes'
import * as trackingKeys from 'actions/trackingKeys'
import { useCollection, useCurrentCollectionId } from 'hooks/menu-collections'
import { useInStockRecipeCoreIds } from 'hooks/menu-recipes/useInStockRecipeCoreIds'
import { useScrollableBasket } from 'routes/Menu/hooks/useScrollableBasket'
import {
  getBasketMenuId,
  getBasketMenuUuid,
  getBasketRecipes,
  getBasketSlotId,
} from 'selectors/basket'
import { getRecipeIdByCoreIdMap } from 'selectors/menu-recipes/getRecipeIdByCoreIdMap'
import { getUTMAndPromoCode } from 'selectors/tracking'

import { useRecipeLimitReached } from '../limits'

export const useAddValidRecipeToBasket = () => {
  const menuRecipes = useSelector(getBasketRecipes)
  const slotId = useSelector(getBasketSlotId)
  const { promoCode, UTM } = useSelector(getUTMAndPromoCode)
  const currentMenuId = useSelector(getBasketMenuId)
  const currentMenuUUID = useSelector(getBasketMenuUuid)
  const recipeIdByCoreIdMap = useSelector(getRecipeIdByCoreIdMap)

  const currentCollectionId = useCurrentCollectionId()
  const currentCollection = useCollection(currentCollectionId)
  const prevRecipes = usePrevious(menuRecipes)
  const inStockRecipes = useInStockRecipeCoreIds()

  const reachedLimit = useRecipeLimitReached(menuRecipes)
  const isScrollableBasketEnabled = useScrollableBasket()
  const atLimit = reachedLimit && !isScrollableBasketEnabled

  const dispatch = useDispatch()

  return React.useCallback(
    (
      recipeId: string,
      view?: string,
      recipeInfo?: {
        position: string
      },
      orderId?: string,
      isOrderRecipe?: boolean, // if this is true, the recipe was already part of the order and we are just initialising the basket
    ) => {
      const outOfStock = !inStockRecipes.has(recipeId as RecipeCoreId)

      const indexOfCollection = currentCollection?.index ?? -1

      if ((atLimit || outOfStock) && !isOrderRecipe) {
        return
      }

      if (recipeInfo) {
        Object.assign(recipeInfo, { collection: currentCollectionId })
      }

      dispatch({
        type: actionTypes.BASKET_RECIPE_ADD,
        recipeId,
        ...recipeInfo,
        orderId,
        trackingData: view
          ? {
              actionType: trackingKeys.addRecipe,
              recipe_id: recipeId,
              recipe_uuid: recipeIdByCoreIdMap[recipeId as RecipeCoreId],
              menu_id: currentMenuId,
              menu_uuid: currentMenuUUID,
              event_screen: view,
              collection_id: currentCollectionId,
              collection_position: indexOfCollection + 1,
              recipe_position: recipeInfo && recipeInfo.position,
              basket_recipe_count: menuRecipes.size + 1, // The action is performed in the same time so the size is not updated yet
            }
          : undefined,
      })

      if (atLimit) {
        dispatch({
          type: actionTypes.BASKET_LIMIT_REACHED,
          limitReached: reachedLimit,
          trackingData: {
            actionType: trackingKeys.basketLimit,
            limitReached: reachedLimit,
            view,
            source: actionTypes.RECIPE_ADDED,
          },
        })
      }

      let recipesCount = 0

      menuRecipes.forEach((count = 0) => {
        recipesCount += count
      })

      if (prevRecipes && prevRecipes.size < 2 && recipesCount > 1 && slotId) {
        dispatch({
          type: actionTypes.BASKET_ELIGIBLE_TRACK,
          trackingData: {
            actionType: trackingKeys.basketEligible,
            ...UTM,
            promoCode,
            menuRecipes,
          },
        })
      }
    },
    [
      atLimit,
      inStockRecipes,
      currentCollection?.index,
      reachedLimit,
      dispatch,
      recipeIdByCoreIdMap,
      currentMenuId,
      currentMenuUUID,
      currentCollectionId,
      menuRecipes,
      prevRecipes,
      slotId,
      UTM,
      promoCode,
    ],
  )
}
